import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  KeyRound,
  LayoutDashboard,
  Search,
  Package,
  Briefcase,
  Users,
  FileText,
  Wrench,
  Fuel,
  BarChart3,
  Activity,
  LifeBuoy,
  Crown,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Zap,
  Calendar,
  Phone,
  Calculator,
  Bot,
  FileImage,
  Receipt,
  CreditCard
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';
import BottomNav from './BottomNav';
import FloatingPhone from './FloatingPhone';
import AnnouncementBanner from './AnnouncementBanner';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Vehicle Lookup', href: '/vehicle-lookup', icon: Search },
  { name: 'Dispatch Center', href: '/dispatch', icon: Zap },
  { name: 'Schedule', href: '/schedule', icon: Calendar },
  { name: 'Phone System', href: '/phone-system', icon: Phone },
  { name: 'Jobs', href: '/jobs', icon: Briefcase },
  { name: 'Customers', href: '/customers', icon: Users },
  { name: 'Quotes', href: '/quotes', icon: FileText },
  { name: 'Invoices', href: '/invoices', icon: Receipt },
  { name: 'Pricing Calculator', href: '/pricing-calculator', icon: Calculator },
  { name: 'Inventory', href: '/inventory', icon: Package },
  { name: 'Equipment', href: '/equipment', icon: Wrench },
  { name: 'Gas & Mileage', href: '/gas-expense', icon: Fuel },
  { name: 'AI Assistant', href: '/ai-assistant', icon: Bot },
  { name: 'Documents', href: '/documents', icon: FileImage },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'Analytics', href: '/analytics', icon: Activity, masterOnly: true },
  { name: 'Tenants', href: '/tenants', icon: Crown, masterOnly: true },
  { name: 'Support Inbox', href: '/support-inbox', icon: LifeBuoy, masterOnly: true },
  { name: 'Team', href: '/team', icon: Users },
  { name: 'Billing', href: '/billing', icon: CreditCard },
];

export default function Layout() {
  const { user, company, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div
      className="flex flex-col bg-background overflow-hidden"
      style={{
        // Safari-safe: 100dvh shrinks with the URL bar, 100vh doesn't.
        height: '100dvh',
        minHeight: '-webkit-fill-available',
      }}
    >
      {/* Top zone: sidebar + main share this row, BottomNav is the row below */}
      <div className="flex-1 relative overflow-hidden min-h-0">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar (absolute inside the top zone, so it can't bleed under BottomNav) */}
        <aside
          className={cn(
            'absolute top-0 left-0 z-50 h-full w-64 bg-card border-r transform transition-transform duration-200 ease-in-out lg:translate-x-0',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 p-6 border-b">
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-primary/5 flex items-center justify-center p-0.5">
              <img src="/brand/logo.png" alt="My Key Boss" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-lg font-bold">My Key Boss</h1>
              <p className="text-xs text-muted-foreground">Locksmith Pro</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="ml-auto lg:hidden"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navigation
              .filter((item) => !item.masterOnly || user?.role === 'super_admin')
              .map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="p-4 border-t">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">{company?.name}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-profile" className="cursor-pointer">
                  <Users className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings" className="cursor-pointer">
                  <Wrench className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/billing')} data-testid="menu-billing" className="cursor-pointer">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Billing
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content area (scrolls inside its own container) */}
      <div className="lg:pl-64 h-full flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="sticky top-0 z-30 bg-card border-b lg:hidden shrink-0">
          <div className="flex items-center justify-between p-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-muted"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md overflow-hidden flex items-center justify-center">
                <img src="/brand/logo.png" alt="My Key Boss" className="w-full h-full object-contain" />
              </div>
              <span className="font-bold">My Key Boss</span>
            </div>
            <div className="w-10" /> {/* Spacer */}
          </div>
        </header>

        {/* Impersonation banner — visible when master is logged in as another user */}
        <ImpersonationBanner />

        {/* Active platform announcements (visible to all authenticated users) */}
        <AnnouncementBanner />

        {/* Scrollable page content — overflow happens HERE, not on body */}
        <main
          className="flex-1 overflow-y-auto overscroll-contain p-4 md:p-6 lg:p-8"
          style={{
            // Safari fix: pad content above iOS home indicator + bottom nav,
            // and account for the address-bar height change.
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 3rem)',
          }}
        >
          <Outlet />
        </main>
      </div>
      </div>

      {/* Bottom Nav (last row of the flex column — never overlaps content) */}
      <BottomNav />
      {/* Floating phone (bottom-left, hidden on public pages) */}
      <FloatingPhone />
    </div>
  );
}

function ImpersonationBanner() {
  const session = (() => {
    try { return JSON.parse(localStorage.getItem('master_session') || 'null'); }
    catch { return null; }
  })();
  if (!session?.token) return null;

  const stop = () => {
    try {
      const u = session.user ? JSON.parse(session.user) : null;
      const c = session.company ? JSON.parse(session.company) : null;
      localStorage.setItem('token', session.token);
      if (u) localStorage.setItem('user', JSON.stringify(u));
      if (c) localStorage.setItem('company', JSON.stringify(c));
      localStorage.removeItem('master_session');
      window.location.href = '/tenants';
    } catch {
      localStorage.removeItem('master_session');
      window.location.reload();
    }
  };

  return (
    <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between text-sm font-semibold" data-testid="impersonation-banner">
      <span>👁️ You are impersonating a tenant user — actions will be attributed to them.</span>
      <button onClick={stop} className="bg-amber-950 text-amber-50 px-3 py-1 rounded font-bold hover:bg-amber-900" data-testid="stop-impersonation">
        Stop impersonating
      </button>
    </div>
  );
}
